import React, { useState, useMemo } from 'react';
import { useCalendar } from '../../hooks/useCalendar';
import { dashboardStyles as s } from '../../styles/dashboardStyles';
import { calendarStyles as c } from '../../styles/calendarStyles';
import { generateCalendarDays, formatDate } from '../../utils/calendarHelpers';

const ACTIONS = {
  receipt: { 
    url: "https://siat.impuestos.gob.bo/v2/launcher/", 
    color: "bg-msc-blue", 
    ring: "ring-msc-blue", 
    bg: "bg-msc-blue/5", 
    text: "text-msc-blue" 
  },
  close: { 
    url: "https://msc-rewards-comercios-pwa.pre.koibanx.link/login", 
    color: "bg-msc-red", 
    ring: "ring-msc-red", 
    bg: "bg-msc-red/5", 
    text: "text-msc-red" 
  },
  payment: { 
    color: "bg-msc-green", 
    ring: "ring-msc-green", 
    bg: "bg-msc-green/5", 
    text: "text-msc-green" 
  },
  today: { ring: "ring-msc-yellow", bg: "bg-msc-yellow/5" },
  default: { ring: "ring-slate-400", bg: "bg-slate-50" }
};

const PLAN_LABELS = { weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual' };

const Calendar = () => {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const { events, userPlan, loading } = useCalendar();

  const now = new Date();
  const todayStr = formatDate(now);
  const isAfterTwoPM = now.getHours() >= 14;

  const calendarDays = useMemo(() => generateCalendarDays(viewDate), [viewDate]);
  const monthName = viewDate.toLocaleString('es-ES', { month: 'long' });

  const handleMonthChange = (offset) => {
    const newDate = new Date(viewDate);
    newDate.setMonth(newDate.getMonth() + offset);
    setViewDate(newDate);
    setSelectedDate(null);
  };

  const getDayConfig = (dateStr, isToday, isPast) => {
    const event = events?.find(e => e.payment === dateStr || e.receipt === dateStr || e.close === dateStr);
    if (!event) return null;

    let config = null;
    if (event.payment === dateStr) config = { ...ACTIONS.payment, label: "DESEMBOLSO", type: 'payment' };
    if (event.receipt === dateStr) config = { ...ACTIONS.receipt, label: "FACTURACIÓN", action: "Facturar", type: 'receipt' };
    if (event.close === dateStr) {
      const expired = isToday && isAfterTwoPM;
      config = { 
        ...ACTIONS.close, 
        label: "CIERRE", 
        action: expired ? "Cerrado" : "Cerrar",
        disabled: expired,
        type: 'close' 
      };
    }

    const shouldBeLocked = isPast || (config?.type === 'close' && isToday && isAfterTwoPM);

    if (shouldBeLocked) {
      return {
        ...config,
        color: "bg-slate-300",
        text: "text-slate-400",
        ring: "ring-slate-300",
        bg: "bg-slate-50",
        disabled: true
      };
    }

    return config;
  };

  return (
    <section id="calendar" className={`${s.section} max-w-full flex flex-col`}>
      <div className={s.headerContainer}>
        <h3 className={s.headerTitle}>Cronograma {loading ? '...' : (PLAN_LABELS[userPlan] || '')}</h3>
        <p className={s.headerSubtitle}>Gestión operativa Sogemsa</p>
      </div>

      {loading ? (
        <div className="py-40 text-center text-slate-300 animate-pulse text-xs font-black uppercase">Sincronizando...</div>
      ) : (
        <div className={c.mainContainer}>
          <div className={c.navBar}>
            <h2 className={c.monthTitle}>
              {monthName} <span className={c.yearLabel}>del</span> {viewDate.getFullYear()}
            </h2>
            <div className={c.controlsWrapper}>
              <button onClick={() => handleMonthChange(-1)} className={c.navBtn}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button onClick={() => handleMonthChange(1)} className={c.navBtn}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          <div className={c.weekHeader}>
            {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'].map(day => (
              <div key={day} className={c.weekDayLabel}>{day}</div>
            ))}
          </div>
          
          <div className={c.grid}>
            {calendarDays.map((item, index) => {
              const dateStr = formatDate(item.date);
              const isToday = dateStr === todayStr;
              const isSelected = selectedDate === dateStr;
              const dateObj = new Date(item.date);
              dateObj.setHours(0,0,0,0);
              const isPast = dateObj < new Date().setHours(0,0,0,0);
              
              const config = item.currentMonth ? getDayConfig(dateStr, isToday, isPast) : null;

              const getSelectionStyles = () => {
                if (!isSelected) return '';
                // Prioridad 1: Si hay evento, manda el color del evento
                if (config) return `${config.ring} ${config.bg}`;
                // Prioridad 2: Si no hay evento pero es hoy, manda amarillo
                if (isToday) return `${ACTIONS.today.ring} ${ACTIONS.today.bg}`;
                // Prioridad 3: Cualquier otro día seleccionado
                return `${ACTIONS.default.ring} ${ACTIONS.default.bg}`;
              };

              return (
                <div 
                  key={`${dateStr}-${index}`} 
                  onClick={() => (item.currentMonth && !isPast) && setSelectedDate(isSelected ? null : dateStr)}
                  className={`${c.cell(item.currentMonth, isPast, isSelected)} ${getSelectionStyles()}`}
                >
                  <div className="flex justify-between items-start">
                    <span className={c.dayNumber(isToday, item.currentMonth)}>{item.day}</span>
                    {isToday && item.currentMonth && (
                      <div className="flex items-center">
                        <div className="sm:hidden w-2 h-2 bg-msc-yellow rounded-full shadow-sm" />
                        <span className="hidden sm:block text-[8px] font-black bg-msc-yellow text-white px-1.5 py-0.5 rounded-full uppercase tracking-tighter shadow-sm">
                          Hoy
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-auto flex flex-col justify-end min-h-[14px] sm:min-h-[32px]">
                    {config && (
                      <div className="w-full">
                        <div className={c.indicatorBar(config.color, isSelected)} />
                        <div className="min-h-[14px] flex items-center">
                          {!isSelected || config.type === 'payment' || config.disabled ? (
                            <span className={c.indicatorText(config.text)}>{config.label}</span>
                          ) : (
                            <button 
                              disabled={config.disabled}
                              onClick={(e) => { e.stopPropagation(); window.open(config.url, '_blank'); }}
                              className={c.actionBtn(config.color)}
                            >
                              {config.action}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default Calendar;